import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import PANDetailsOutputComponent from '../output/PANDetailsOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const PAN_REGEX = /^[A-Z]{5}\d{4}[A-Z]$/;

const PAN_CATEGORIES = {
    P: { type: 'Individual', icon: 'pi-user' },
    C: { type: 'Company', icon: 'pi-building' },
    H: { type: 'HUF', icon: 'pi-users' },
    F: { type: 'Firm', icon: 'pi-briefcase' },
    A: { type: 'AOP', icon: 'pi-sitemap' },
    T: { type: 'Trust', icon: 'pi-shield' },
    B: { type: 'BOI', icon: 'pi-globe' },
    L: { type: 'Local Authority', icon: 'pi-map-marker' },
    J: { type: 'Artificial Juridical Person', icon: 'pi-id-card' },
    G: { type: 'Government', icon: 'pi-flag' },
};

const PANDetailsInputComponent = ({ serviceId: serviceIdProp, serviceDetails: initialServiceDetails, leadId } = {}) => {
    // Standalone /reports route reads serviceId from URL; embedded use (Lead
    // Wizard's verification Sidebar) passes it as a prop. Prop wins, URL is
    // the fallback — same pattern as SimpleReportForm.
    const params = useParams();
    const serviceId = serviceIdProp ?? params.serviceId;
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ panNumber: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [serviceDetails, setServiceDetails] = useState(initialServiceDetails || null);
    const [fetchingService, setFetchingService] = useState(!initialServiceDetails);

    useEffect(() => {
        if (!serviceId) {
            setFetchingService(false);
            return;
        }
        const load = async () => {
            setFetchingService(true);
            try {
                const res = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (res.data.success && res.data.data) setServiceDetails(res.data.data);
            } catch (error) {
                console.error('Load service details failed:', error?.message || 'Unknown error');
                setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' });
            } finally {
                setFetchingService(false);
            }
        };
        load();
    }, [serviceId]);

    const panCategory = useMemo(() => {
        const pan = formData.panNumber;
        if (pan.length < 4) return null;
        return PAN_CATEGORIES[pan[3]] || null;
    }, [formData.panNumber]);

    const isValidPan = PAN_REGEX.test(formData.panNumber);

    const handlePanChange = (e) => {
        const value = (e.target.value || '').toUpperCase().trim();
        setFormData((prev) => ({ ...prev, panNumber: value }));
        if (errors.panNumber) setErrors((prev) => ({ ...prev, panNumber: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.panNumber) next.panNumber = 'PAN number is required';
        else if (!PAN_REGEX.test(formData.panNumber)) next.panNumber = 'Invalid PAN format (e.g., ABCDE1234F)';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submitVerification = async () => {
        setLoading(true);
        try {
            const payload = {
                panNumber: formData.panNumber,
                service_id: serviceDetails?.id || 1,
                api_provider_service_id: serviceId,
                ...(leadId ? { lead_id: parseInt(leadId, 10) } : {}),
            };
            const res = await axiosInstance.post('/tartan/get-user-details-from-pan', payload);

            if (!res.data.success) throw new Error(res.data.message || 'Verification failed');

            setReportData({
                success: res.data.success,
                message: res.data.message,
                code: res.data.code,
                response: res.data.data,
                data: {
                    ...res.data.data,
                    remaining_balance: res.data.data?.remaining_balance ?? res.data.remaining_balance,
                },
                pdf_download_url: res.data.pdf_download_url || res.data.data?.pdf_download_url,
                pdf_password_hint: res.data.pdf_password_hint || res.data.data?.pdf_password_hint,
            });

            toast.current?.show({
                severity: 'success',
                summary: 'PAN verified',
                detail: 'Report fetched successfully',
                life: 2000,
            });

            try {
                const refreshed = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (refreshed.data.success && refreshed.data.data) setServiceDetails(refreshed.data.data);
            } catch (refreshErr) {
                console.error('Credit refresh failed:', refreshErr?.message || 'Unknown error');
            }
        } catch (error) {
            console.error('PAN verify failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check your PAN number and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid PAN number. Please enter a valid PAN.',
            };
            let detail = map[status] || error.response?.data?.message || error.message || 'Verification failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({
                severity: 'error',
                summary: 'Verification failed',
                detail,
                life: 3500,
            });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;

        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({
                severity: 'warn',
                summary: 'Validation error',
                detail: 'Please fix the highlighted fields',
                life: 3000,
            });
            return;
        }
        setErrors({});
        submittingRef.current = true;

        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({
                severity: 'error',
                summary: 'Insufficient credits',
                detail: `Required: ${required}, Available: ${available}`,
                life: 3500,
            });
            return;
        }

        // Scope to a named group so the dialog ONLY fires the
        // <ConfirmDialog group="pan-v1-confirm" /> rendered below — without
        // it, the Lead Wizard's global <ConfirmDialog /> also catches the
        // event and shows a duplicate dialog.
        confirmDialog({
            group: 'pan-v1-confirm',
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">PAN:</span> <span className="text-900 font-medium">{formData.panNumber}</span></div>
                    {panCategory && (
                        <div className="mb-2"><span className="text-600">Type:</span> <span className="text-900 font-medium">{panCategory.type}</span></div>
                    )}
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm PAN verification',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, verify',
            rejectLabel: 'Cancel',
            accept: submitVerification,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleVerifyAnother = () => {
        setReportData(null);
        setFormData({ panNumber: '', consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog group="pan-v1-confirm" />

            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-id-card"
                        title="PAN Verification"
                        subtitle="Instant PAN verification with the Income Tax Department database"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />

                    {/* Form OR Output */}
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="panNumber" className="block font-medium text-sm mb-2">
                                            PAN Number <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-id-card" />
                                            <InputText
                                                id="panNumber"
                                                value={formData.panNumber}
                                                onChange={handlePanChange}
                                                placeholder="ABCDE1234F"
                                                maxLength={10}
                                                autoCapitalize="characters"
                                                spellCheck={false}
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm', { 'p-invalid': !!errors.panNumber })}
                                                aria-describedby="pan-help"
                                                aria-invalid={!!errors.panNumber}
                                            />
                                        </IconField>
                                        {errors.panNumber && (
                                            <small id="pan-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.panNumber}
                                            </small>
                                        )}
                                        {!errors.panNumber && isValidPan && (
                                            <small className="text-green-500 block mt-1">
                                                <i className="pi pi-check-circle mr-1" aria-hidden="true" />
                                                Valid PAN format
                                                {panCategory && <> · {panCategory.type}</>}
                                            </small>
                                        )}
                                    </div>
                                </div>

                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={handleConsentChange}
                                    error={errors.consent}
                                    reportType="pan"
                                />

                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label="Verify PAN"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={loading}
                                        disabled={loading}
                                        size="small"
                                    />
                                </div>
                            </form>

                            {/* Collapsible PAN format info — P8 */}
                            <Accordion className="mt-4">
                                <AccordionTab
                                    header={
                                        <span className="flex align-items-center gap-2 text-sm font-medium">
                                            <i className="pi pi-info-circle text-primary" aria-hidden="true" />
                                            Understanding PAN format
                                        </span>
                                    }
                                >
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <p className="text-700 text-sm m-0 mb-2"><strong>Structure:</strong> ABCDE1234F</p>
                                            <ul className="text-600 text-sm line-height-3 pl-3 m-0">
                                                <li>First 3 chars — alphabetic series (AAA–ZZZ)</li>
                                                <li>4th char — entity status (P, C, H, …)</li>
                                                <li>5th char — first letter of surname/name</li>
                                                <li>Next 4 chars — sequential numbers (0001–9999)</li>
                                                <li>Last char — alphabetic check digit</li>
                                            </ul>
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <p className="text-700 text-sm m-0 mb-2"><strong>Entity types (4th character):</strong></p>
                                            <div className="grid text-sm">
                                                <div className="col-6 flex flex-column gap-2">
                                                    <span><Tag severity="info" value="P" className="mr-2" /> Individual</span>
                                                    <span><Tag severity="success" value="C" className="mr-2" /> Company</span>
                                                    <span><Tag severity="warning" value="H" className="mr-2" /> HUF</span>
                                                    <span><Tag severity="danger" value="F" className="mr-2" /> Firm</span>
                                                </div>
                                                <div className="col-6 flex flex-column gap-2">
                                                    <span><Tag severity="info" value="T" className="mr-2" /> Trust</span>
                                                    <span><Tag severity="success" value="A" className="mr-2" /> AOP</span>
                                                    <span><Tag severity="warning" value="G" className="mr-2" /> Government</span>
                                                    <span><Tag severity="danger" value="L" className="mr-2" /> Local Authority</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </AccordionTab>
                            </Accordion>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Verify another PAN"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleVerifyAnother}
                                />
                            </div>
                            <PANDetailsOutputComponent
                                reportData={reportData}
                                serviceInfo={serviceDetails}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default PANDetailsInputComponent;
